// The keyboard's half of the viewfinder (SPEC-viewfinder §1.7, owner's ruling 9).
//
// Its own file, and not `./camera.ts`, for the reason `./mark-drag.ts` is its
// own: the camera module is the RULES a gesture obeys — who owns the map, when
// a gesture is finished, what settles — and this is one input driving them. It
// adds no rule of its own. A held arrow is one gesture and therefore one Back,
// by the same §1.7 sentence that makes a flick caught mid-glide one gesture;
// the only thing this file contributes to that sentence is the fact that a key
// is still down, which `Live.keys` carries beside `Live.pointers`.
//
// MapLibre's own keyboard handler is OFF (`offer`, `./camera.ts`). It pans by a
// fixed 100 px and zooms with `+`/`-`, and neither is this product: the step is
// a fraction of the RADIUS, and the radius belongs to the slider, which keeps
// its own arrow behaviour (#241 D6).
import type { CameraDeps, Live } from './camera.ts';

/** A press moves the centre by a radius over this (§1.7: 375 m at r = 3 km). */
const STEP = 8;
/** With `Shift`, by a radius over this — half the radius, the ruling's own word. */
const SHIFT_STEP = 2;

/**
 * Which way the CAMERA goes for each arrow, in screen pixels. `↑` is north, so
 * it is a negative y: the ground under the viewfinder moves down, which is what
 * a resident means by „look further up".
 */
const WAYS: Record<string, readonly [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/**
 * What the block tells a screen reader it listens for (§5, the „Map block,
 * keyboard" row).
 *
 * The `Shift` pairs are named too, and not as a flourish: the „Klawiatura."
 * card promises „Strzałki przesuwają środek mapy, **z Shiftem dalej**", so the
 * half-radius step is a stated feature — and the one attribute whose whole job
 * is to announce shortcuts is not where it may go missing.
 */
const SHORTCUTS =
  'ArrowUp ArrowDown ArrowLeft ArrowRight Shift+ArrowUp Shift+ArrowDown Shift+ArrowLeft Shift+ArrowRight';

/**
 * The arrow keys on the focused map block.
 *
 * @param finish the camera owner's own end-of-gesture, called when the last
 *   key comes up. The move itself settles nothing: `panBy` raises its `moveend`
 *   inside the `keydown`, while the key is still down, so `finish` reads
 *   `Live.keys` as non-empty and stands back exactly as it does for a finger
 *   still on the glass. That is what coalesces a held arrow into one entry.
 */
export function hearArrows(deps: CameraDeps, live: Live, finish: () => void): { refresh: () => void } {
  const { block } = deps;

  block.addEventListener('keydown', (e) => step(deps, live, e));

  /**
   * A key is no longer down. Every path that can end a press comes here: the
   * key coming up, focus leaving the block, and the tab stop being taken away.
   *
   * `blur` is not belt and braces. A `keyup` that lands on another element —
   * the resident tabbed away with the arrow still down, or the window lost
   * focus — never reaches this block, and a key left in the set would hold the
   * gesture open for ever: the next settle would never write, and the pan would
   * be silently lost.
   */
  function release(key?: string) {
    if (key === undefined) live.keys.clear();
    else live.keys.delete(key);
    if (live.keys.size === 0) finish();
  }

  block.addEventListener('keyup', (e) => {
    if (live.keys.has(e.key)) release(e.key);
  });
  block.addEventListener('blur', () => release());

  return {
    /**
     * The tab stop, re-asked with the handler set it belongs to (§0.2's D-f
     * insertion, §6). One stop between the top bar and the radius slider — the
     * block comes first in the document, so reading order and focus order are
     * the same thing (WCAG 2.4.3).
     *
     * The name is the one the block already has: `Co jest blisko`, from the
     * visually hidden `<h2>` it is labelled by (§7.1). The marks inside it stay
     * `tabindex="-1"` — the rail is still the keyboard path through the data.
     */
    refresh() {
      // MapLibre gives its canvas `tabindex="0"` of its own, so the block was
      // TWO stops — the region, then „Mapa Poznania" — and the second one does
      // nothing at all, because `offer` disables MapLibre's keyboard handler.
      // Measured; it is why this is here rather than assumed away.
      deps.map.getCanvas().tabIndex = -1;
      if (deps.offered() && deps.disc() > 0) {
        block.setAttribute('tabindex', '0');
        block.setAttribute('aria-keyshortcuts', SHORTCUTS);
        return;
      }
      block.removeAttribute('tabindex');
      block.removeAttribute('aria-keyshortcuts');
      // A block that stops being a focus stop mid-press must not leave the
      // gesture open — the dial arrives from a drag on the grip, not from a
      // keyup.
      release();
    },
  };
}

/**
 * One press: the camera moves, and nothing settles while the key is down.
 *
 * Outside {@link hearArrows} so that factory stays inside ADR-0015's 80 lines
 * — and it costs nothing, because a press needs no memory between presses. The
 * one thing it does remember is `Live.keys`, which belongs to the gesture and
 * not to this file.
 */
function step(deps: CameraDeps, live: Live, e: KeyboardEvent) {
  // The slider sits INSIDE this block, so its own arrow keys bubble through
  // here (#241 D6). Only a press on the block itself is the camera's.
  if (e.target !== deps.block) return;
  const way = WAYS[e.key];
  if (!way) return;
  // §6, ruled: no arrow keys in the dial and none with a basemap that never
  // loaded — the same predicate that takes the tab stop away.
  if (!deps.offered() || deps.disc() === 0) return;
  // Or the page scrolls under the resident as well as the map moving.
  e.preventDefault();
  live.keys.add(e.key);
  // The disc's diameter is exactly twice the radius (`zoomFor`, §6.3), so a
  // radius over eight is `disc / 16` PIXELS — exact at every radius and every
  // latitude, where metres would need the `cos(lat)` correction that already
  // leaked a drifting `r` into the address once (`Live.startZoom`). Panning in
  // pixels also holds the zoom bit-exactly, so `zoomed()` stays false and an
  // arrow can never write a radius (§1.7).
  const px = deps.disc() / 2 / (e.shiftKey ? SHIFT_STEP : STEP);
  // Instant, not eased: an eased step would still be running when the next
  // repeat arrives, and `easeTo` calls `map.stop()` first — so the camera would
  // spend a held key restarting itself. The clamp of §1.3 is applied by the
  // owner's own `move` handler, exactly as it is for a finger.
  deps.map.panBy([way[0] * px, way[1] * px], { duration: 0 });
}
